<template>
    <button
        type="button"
        class="menu-delete-item"
        @click="assistentDeleteDialog?.dialog?.openOverlay()"
        @keydown.enter="assistentDeleteDialog?.dialog?.openOverlay()"
    >
        <flex-container align-items="center">
            <text-decorator weight="medium">
                {{ props.title }}
            </text-decorator>
        </flex-container>
    </button>
    <assistent-delete-dialog ref="assistentDeleteDialog" @confirm="onDeleteConfirmed" />
</template>

<script setup lang="ts">
    import { TextDecorator } from '@keyvue/one-ui';
    import { ref } from 'vue';
    import { useRouter } from 'vue-router';
    import AssistentDeleteDialog from '@/ai/assistent/components/dialog/AssistentDeleteDialog.vue';
    import { AssistentId, DeleteAssistentCommand } from '@keyvue/one-api-client';
    import AssistentDeletedEvent, { type AssistentDeletedEventParameters } from '@/ai/assistent/events/AssistentDeletedEvent';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import useNotify from '@/services/keyvue-ui/composables/useNotify';
    import useEventBus from '@/services/eventbus/composables/useEventBus';

    const { client } = useClient();
    const router = useRouter();
    const eventBus = useEventBus();
    const { notifyDeleted, notifyError } = useNotify();

    export interface Props {
        title: string,
    }

    const props = defineProps<Props>();

    const assistentDeleteDialog = ref<InstanceType<typeof AssistentDeleteDialog>>();

    async function onDeleteConfirmed() {
        try {
            await client.dispatch(
                new DeleteAssistentCommand(
                    new AssistentId(router.currentRoute.value.params.assistentId as string),
                ),
            );

            notifyDeleted('De AI Agent is verwijderd');
            eventBus.dispatch<AssistentDeletedEventParameters>(AssistentDeletedEvent, {
                assistentId: router.currentRoute.value.params.assistentId as string,
            });
            await router.push({ name: 'assistent-dashboard' });
        } catch (error) {
            notifyError(
                new Error('We konden de AI Agent niet verwijderen'),
            );
        }
    }
</script>

<style scoped lang="scss">
.menu-delete-item {
    width: calc(100% - size(xl));
    padding: size(sm);
    color: color(red-500);
    background-color: transparent;
    transition: background-color 0.1s ease;
    border-radius: size(xs);
    cursor: pointer;
    user-select: none;
    border:0;
    position: relative;

    &:before {
        inset: 0;
        content: '';
        position: absolute;
        border-radius: size(xs);
        background-color: transparent;
        transition: background-color 0.1s ease;
        opacity: .15;
    }

    &:hover {
        background-color: white;

        &:before {
            background-color: var(--theme-colors-default--red-300);
        }
    }

    &:disabled {
        color: lightgray;
    }
}
</style>
